/**
 * About page with Research Foundations.
 *
 * Two sections:
 * 1. Why We Track What We Track — the developmental research behind
 *    the five Perspective tiers
 * 2. How We Design Memorable Experiences — the evidence base for the
 *    future activity planner
 *
 * Tone: warm and matter-of-fact per STYLE_GUIDE §5. Written for
 * parents, not academics — but every claim links to a real source.
 */

import { Link } from "react-router-dom";

import { BrandMark } from "@/components/BrandMark";
import { Wordmark } from "@/components/Wordmark";

export function AboutPage() {
  return (
    <main className="max-w-[780px] mx-auto px-6 md:px-11 pt-14 md:pt-20 pb-24 flex flex-col gap-16">
      <header className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <BrandMark />
          <Wordmark size="hero" />
        </div>
        <h1 className="font-serif text-display-md text-ink">
          The research behind{" "}
          <em className="italic font-normal text-coral-deep">the numbers</em>
        </h1>
        <p className="font-sans text-[15px] text-ink-muted leading-[1.7] max-w-[640px]">
          Make It Count isn't guessing. Every perspective, every developmental
          window, and every future activity suggestion is grounded in
          peer-reviewed research from developmental psychology, memory science,
          and family studies. Here's what we drew on and why.
        </p>
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* SECTION 1: Why we track what we track                            */}
      {/* ---------------------------------------------------------------- */}

      <section className="flex flex-col gap-8">
        <SectionHeader
          eyebrow="Perspectives"
          title="Why we track what we track"
        />

        <Prose>
          Make It Count organizes your remaining time with your kids into five
          tiers. Each tier reflects a distinct phase of childhood development
          documented in decades of research. The goal isn't to create anxiety —
          it's to make the invisible visible so you notice what's here
          before it's gone.
        </Prose>

        <TierBlock
          tier={1}
          name="Big Window"
          subtitle="The 18-year counters"
          description="Summers, winter breaks, spring breaks, school drop-offs, birthday parties. These run from birth to 18 and form the broadest frame of childhood. The 18 Summers concept draws on seasonal developmental frameworks that show how annual rituals anchor a child's sense of family identity."
          sources={[
            {
              label: "Fiese et al. — 50 Years of Family Routines and Rituals Research (APA, 2002)",
              url: "https://www.apa.org/pubs/journals/releases/fam-164381.pdf",
            },
            {
              label: "Selman (2024) — Routines and Child Development: A Systematic Review",
              url: "https://onlinelibrary.wiley.com/doi/10.1111/jftr.12549",
            },
          ]}
        />

        <TierBlock
          tier={2}
          name="Fleeting Magic"
          subtitle="The short-window rituals"
          description="Santa belief, tooth fairy visits, bedtime stories, tuck-ins, Easter egg hunts, trick-or-treat, piggyback rides, read-to-me evenings. Each of these has a developmental window that opens and closes — typically between ages 2 and 10. The windows are anchored in research on magical thinking (which peaks around ages 3–8), the emergence of independent bedtime routines (around age 10), and the physical growth curves that end the piggyback era."
          sources={[
            {
              label: "Woolley & Ghossainy — Revisiting the Fantasy-Reality Distinction (Child Development Perspectives, 2013)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4025992/",
            },
            {
              label: "Development of Episodic and Autobiographical Memory (PMC, 2015)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4669902/",
            },
          ]}
        />

        <TierBlock
          tier={3}
          name="Peer Shift"
          subtitle="The 10–13 transition"
          description="Hand-holding in public, 'play with me' years, Saturday-morning pancakes, family movie nights, parent-led vacations. These perspectives close when the peer shift happens — the developmental transition around ages 10–13 where friends replace parents as the child's preferred social world. Erikson's Industry vs. Inferiority stage (ages 6–12) gives way to Identity vs. Role Confusion, and the child's social center of gravity moves outside the home."
          sources={[
            {
              label: "Erikson's Stages of Psychosocial Development (NCBI StatPearls)",
              url: "https://www.ncbi.nlm.nih.gov/books/NBK556096/",
            },
            {
              label: "Kim (2025) — Family Bonding Activity Type and Child Social-Emotional Development",
              url: "https://onlinelibrary.wiley.com/doi/10.1111/chso.12974",
            },
          ]}
        />

        <TierBlock
          tier={4}
          name="Everyday"
          subtitle="The high-cadence counters"
          description="Family dinners, bedtime routines, morning routines, drives to school, weekends at home. These are the big numbers — hundreds to thousands — that make the passage of time tangible. Longitudinal research shows that family meal quality at age 6 predicts fitness, lower aggression, and fewer behavioral problems at age 10. The sheer volume of these moments is the point: each one is ordinary, but the total is finite."
          sources={[
            {
              label: "Family Meals and Child Academic and Behavioral Outcomes (PMC, 2012)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3498594/",
            },
            {
              label: "Systematic Review: Family Meal Frequency and Psychosocial Outcomes (PMC, 2015)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4325878/",
            },
            {
              label: "Family Meal Environment Quality and Later Well-Being (PubMed, 2017)",
              url: "https://pubmed.ncbi.nlm.nih.gov/29227338/",
            },
          ]}
        />

        <TierBlock
          tier={5}
          name="Big Picture"
          subtitle="The runway"
          description="Days until 18 and childhood runway remaining. These are the calibration perspectives — the raw numbers that put every other counter in context. The percentage meter draws on the broader developmental psychology principle that parents spend roughly 80% of their total lifetime hours with a child before the child turns 18."
          sources={[
            {
              label: "Piaget's Stages of Cognitive Development (Simply Psychology)",
              url: "https://www.simplypsychology.org/piaget.html",
            },
          ]}
        />
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* SECTION 2: How we design memorable experiences                   */}
      {/* ---------------------------------------------------------------- */}

      <section className="flex flex-col gap-8">
        <SectionHeader
          eyebrow="Activity planning"
          title="How we'll help you make it count"
        />

        <Prose>
          A future version of Make It Count will suggest age-aware,
          season-aware activities tailored to each child — not generic
          Pinterest lists, but experiences grounded in what the research
          says actually sticks. Here's the evidence we're building on.
        </Prose>

        <ResearchBlock
          title="Children form lasting memories differently at each age"
          body="The hippocampus and prefrontal cortex don't reach functional maturity until around age 3–4. Before that, experiences build neural pathways but rarely survive as conscious memories into adulthood. By age 5–6, autobiographical memory stabilizes to adult-quality encoding. Activities for toddlers serve development; activities for school-age children build memories they'll carry."
          sources={[
            {
              label: "Development of Episodic and Autobiographical Memory (PMC, 2015)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4669902/",
            },
            {
              label: "Childhood Amnesia — A Prospective Investigation (PMC, 2014)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4025992/",
            },
          ]}
        />

        <ResearchBlock
          title="Novelty encodes more strongly than repetition"
          body="When children encounter something new, the hippocampus releases dopamine, strengthening long-term memory formation. A study of elementary school children found that a single novel experience within an hour of a learning event significantly improved recall. The implication: one out-of-the-ordinary afternoon creates a stronger memory than ten routine ones."
          sources={[
            {
              label: "Novelty Improves Memory in School Scenarios (PMC, 2020)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7000375/",
            },
            {
              label: "Novel Experience Improves Memory in Elementary Children (PLOS ONE, 2013)",
              url: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0066875",
            },
          ]}
        />

        <ResearchBlock
          title="How you talk about it afterward matters as much as the experience itself"
          body="Robyn Fivush's research at Emory University shows that parents who practice 'elaborative reminiscing' — asking detailed questions, using emotional language, co-constructing the story of what happened — produce children with stronger autobiographical memory, better emotion regulation, and a more secure sense of self. The conversation after the adventure is part of the adventure."
          sources={[
            {
              label: "Fivush — Family Reminiscing Style and Child Well-Being (PMC, 2015)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4687742/",
            },
            {
              label: "Maternal Reminiscing as Critical to Emotion Socialization (PMC, 2023)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10163791/",
            },
          ]}
        />

        <ResearchBlock
          title="Peak moments and good endings shape what we remember"
          body="Daniel Kahneman's peak-end rule shows that we judge experiences by their most intense moment and how they ended, not by their average. For family activities, this means building in a 'wow' moment (the summit view, the first bite of what you baked, the surprise reveal) and closing with something warm (a debrief question, a group photo, dessert)."
          sources={[
            {
              label: "Peak-End Rule — Kahneman & Fredrickson (Wikipedia)",
              url: "https://en.wikipedia.org/wiki/Peak%E2%80%93end_rule",
            },
          ]}
        />

        <ResearchBlock
          title="The right challenge level creates flow"
          body="Csikszentmihalyi's flow research shows that deep engagement — the state where time disappears and a child is completely absorbed — occurs when a challenge slightly exceeds the child's current skill. Too easy and they're bored; too hard and they're anxious. Age-appropriate activities should have a stretch element the child can visibly overcome."
          sources={[
            {
              label: "Flow Theory — Investigating the Flow Experience (PMC, 2020)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7033418/",
            },
          ]}
        />

        <ResearchBlock
          title="Seven experiences predict lifelong mental health"
          body="Bethell and Sege's 2019 study in JAMA Pediatrics identified seven Positive Childhood Experiences (PCEs) that independently predict adult mental health — even after controlling for adverse experiences. The seven: feeling able to talk to family about feelings, family standing by you in hard times, community traditions, school belonging, friend support, two non-parent adults who care, and feeling safe at home. Activities that create contexts for these experiences have measurable long-term impact."
          sources={[
            {
              label: "Bethell & Sege (2019) — PCEs and Adult Mental Health (JAMA Pediatrics / PMC)",
              url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6735495/",
            },
            {
              label: "Why PCEs Are Important — Tufts HOPE",
              url: "https://positiveexperience.org/blog/why-pces-are-important-for-all-children-and-families/",
            },
          ]}
        />

        <ResearchBlock
          title="Nature is disproportionately effective"
          body="A systematic review of greenspace effects on children found that nature exposure improves working memory, cognitive flexibility, and attentional control. Longer, less frequent sessions outdoors are more beneficial than short, frequent ones. Outdoor experiences combine novelty, sensory richness, and physical challenge in a package that's hard to replicate indoors."
          sources={[
            {
              label: "Nature and Cognitive Functioning in Children — Systematic Review (Springer, 2022)",
              url: "https://link.springer.com/article/10.1007/s10648-022-09658-5",
            },
            {
              label: "APA — Nurtured by Nature (2020)",
              url: "https://www.apa.org/monitor/2020/04/nurtured-nature",
            },
          ]}
        />
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* FOOTER                                                           */}
      {/* ---------------------------------------------------------------- */}

      <footer className="border-t border-edge-soft pt-8 flex flex-col gap-4">
        <p className="font-sans text-[13px] text-ink-muted max-w-[640px] leading-[1.7]">
          Make It Count is built by{" "}
          <span className="text-ink-soft font-medium">Rogue Wolf Advanced Research LLC</span>.
          The numbers carry the weight; the captions don't add guilt. We
          believe that making time visible is the first step toward using
          it well.
        </p>
        <Link
          to="/dashboard"
          className="inline-flex font-sans text-[14px] text-coral-deep font-medium hover:underline"
        >
          ← Back to dashboard
        </Link>
      </footer>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function SectionHeader({
  eyebrow,
  title,
}: {
  eyebrow: string;
  title: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
        {eyebrow}
      </div>
      <h2 className="font-serif text-[28px] md:text-display-md text-ink">
        {title}
      </h2>
    </div>
  );
}

function Prose({ children }: { children: string }) {
  return (
    <p className="font-sans text-[15px] text-ink-soft leading-[1.7] max-w-[640px]">
      {children}
    </p>
  );
}

interface Source {
  label: string;
  url: string;
}

function TierBlock({
  tier,
  name,
  subtitle,
  description,
  sources,
}: {
  tier: 1 | 2 | 3 | 4 | 5;
  name: string;
  subtitle: string;
  description: string;
  sources: Source[];
}) {
  return (
    <div className="rounded-lg bg-surface border border-edge px-5 md:px-6 py-5 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span className="font-mono tabular-nums text-[22px] font-semibold text-coral-deep">
          {tier}
        </span>
        <div>
          <div className="font-serif text-[18px] text-ink">{name}</div>
          <div className="font-sans text-[12px] text-ink-muted italic">
            {subtitle}
          </div>
        </div>
      </div>
      <p className="font-sans text-[14px] text-ink-soft leading-[1.7]">
        {description}
      </p>
      <SourceList sources={sources} />
    </div>
  );
}

function ResearchBlock({
  title,
  body,
  sources,
}: {
  title: string;
  body: string;
  sources: Source[];
}) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-serif text-[18px] text-ink">{title}</h3>
      <p className="font-sans text-[14px] text-ink-soft leading-[1.7] max-w-[640px]">
        {body}
      </p>
      <SourceList sources={sources} />
    </div>
  );
}

function SourceList({ sources }: { sources: Source[] }) {
  return (
    <div className="flex flex-col gap-1 mt-1">
      {sources.map((s) => (
        <a
          key={s.url}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-sans text-[12px] text-coral-deep hover:underline leading-snug"
        >
          {s.label} ↗
        </a>
      ))}
    </div>
  );
}
